import { useEffect, useMemo, useRef, useState } from 'react'
import { ArrowDown, ArrowUp, ChevronLeft, Monitor, Plus, Smartphone } from 'lucide-react'
import { Shell, useShellMeta } from './Shell.js'
import { ToastProvider, useToast, apiGet, adminMutate, usePermissions } from './primitives.js'
import { useSaveBar } from '../shell/editing.js'
import { navigate } from '../router.js'
import { SettingsCrumb } from './SettingsCrumb.js'

/**
 * ÉDITEUR DE MODÈLE (LOT 9) — /settings/communications/templates/:id.
 *
 * Builder RAISONNABLE : champs texte + variables whitelistées + blocs
 * activables/réordonnables (Monter/Descendre — jamais de drag obligatoire),
 * thème, et un aperçu temps réel rendu PAR LE SERVEUR (les données
 * d'aperçu sont clairement fictives). Aucun HTML libre : le mode « code
 * avancé » est volontairement différé.
 */

interface Template {
  id: string; key: string | null; name: string; description: string | null
  channel: 'IN_APP' | 'EMAIL' | 'PUSH'; event: string; is_system: boolean
  subject: string | null; preheader: string | null; title: string | null; body: string
  cta_label: string | null; cta_link: string | null; footer: string | null
  theme_id: string | null; blocks: { type: string; enabled: boolean }[] | null
}
interface ThemeRow { id: string; name: string; is_default: boolean; config: Record<string, unknown> }
interface VariableDef { key: string; label: string; group: string }

const BLOCK_LABELS: Record<string, string> = {
  HEADER: 'En-tête', STATUS_CARD: 'Carte d’état', TEXT: 'Texte',
  METRIC: 'Mesure', BUTTON: 'Bouton', DIVIDER: 'Séparateur', FOOTER: 'Pied de page',
}
const DEFAULT_BLOCKS = ['HEADER', 'STATUS_CARD', 'TEXT', 'METRIC', 'BUTTON', 'DIVIDER', 'FOOTER']
  .map((t) => ({ type: t, enabled: t !== 'DIVIDER' }))

type Draft = Omit<Template, 'id' | 'key' | 'is_system' | 'channel' | 'event'>

function Inner({ templateId }: { templateId: string }) {
  const toast = useToast()
  const { permissions, authenticated } = usePermissions()
  const canManage = authenticated && permissions.includes('communication.settings.manage')
  const [tpl, setTpl] = useState<Template | null>(null)
  const [themes, setThemes] = useState<ThemeRow[]>([])
  const [variables, setVariables] = useState<VariableDef[]>([])
  const [draft, setDraft] = useState<Draft | null>(null)
  const [initial, setInitial] = useState<string>('')
  const [mode, setMode] = useState<'desktop' | 'mobile'>('desktop')
  const [preview, setPreview] = useState<{ subject: string; html: string; text: string } | null>(null)
  const [showText, setShowText] = useState(false)
  const [picker, setPicker] = useState(false)
  const bodyRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    apiGet<Template>(`/api/v1/communications/templates/${templateId}`).then((t) => {
      const d: Draft = {
        name: t.name, description: t.description, subject: t.subject, preheader: t.preheader,
        title: t.title, body: t.body, cta_label: t.cta_label, cta_link: t.cta_link,
        footer: t.footer, theme_id: t.theme_id, blocks: t.blocks,
      }
      setTpl(t); setDraft(d); setInitial(JSON.stringify(d))
    }).catch(() => toast('error', 'Modèle introuvable.'))
    apiGet<{ items: ThemeRow[] }>('/api/v1/communications/themes').then((j) => setThemes(j.items)).catch(() => undefined)
    apiGet<{ variables: VariableDef[] }>('/api/v1/communications/templates').then((j) => setVariables(j.variables)).catch(() => undefined)
  }, [templateId]) // eslint-disable-line react-hooks/exhaustive-deps

  /* Aperçu serveur, temps réel (débouncé). */
  useEffect(() => {
    if (!draft || tpl?.channel !== 'EMAIL') return
    const t = setTimeout(() => {
      void adminMutate<{ error?: string; subject?: string; html?: string; text?: string; message?: string }>(
        'POST', '/api/v1/communications/templates/preview', draft,
      ).then((r) => {
        if (r.status < 300 && r.data.html) setPreview(r.data as { subject: string; html: string; text: string })
      })
    }, 450)
    return () => clearTimeout(t)
  }, [draft, tpl])

  const dirty = draft !== null && JSON.stringify(draft) !== initial
  useSaveBar({
    dirty: dirty && canManage,
    invalid: draft && !draft.name.trim() ? 'Le modèle a besoin d’un nom.' : draft && !draft.body.trim() ? 'Le corps du message est requis.' : null,
    onSave: async () => {
      if (!draft) return 'Rien à enregistrer.'
      const r = await adminMutate<{ error?: string; message?: string }>('PUT', `/api/v1/communications/templates/${templateId}`, draft)
      if (r.status < 300) { setInitial(JSON.stringify(draft)); return true }
      return r.data.message ?? 'L’enregistrement a échoué.'
    },
    onCancel: () => setDraft(JSON.parse(initial || 'null')),
    successMessage: 'Modèle enregistré.',
  })

  const upd = (p: Partial<Draft>) => setDraft((d) => d ? { ...d, ...p } : d)

  const blocks = useMemo(() => draft?.blocks && draft.blocks.length > 0 ? draft.blocks : DEFAULT_BLOCKS, [draft])
  const moveBlock = (i: number, dir: -1 | 1) => {
    const next = [...blocks]
    const j = i + dir
    if (j < 0 || j >= next.length) return
    ;[next[i], next[j]] = [next[j]!, next[i]!]
    upd({ blocks: next })
  }

  const insertVar = (key: string) => {
    const v = `{{${key}}}`
    const el = bodyRef.current
    if (el && draft) {
      const start = el.selectionStart ?? draft.body.length
      const end = el.selectionEnd ?? draft.body.length
      upd({ body: draft.body.slice(0, start) + v + draft.body.slice(end) })
      setTimeout(() => { el.focus(); el.setSelectionRange(start + v.length, start + v.length) }, 0)
    } else if (draft) {
      upd({ body: draft.body + v })
    }
    setPicker(false)
  }

  const varGroups: { group: string; items: VariableDef[] }[] = []
  for (const v of variables) {
    const g = varGroups.find((x) => x.group === v.group)
    if (g) g.items.push(v)
    else varGroups.push({ group: v.group, items: [v] })
  }

  if (!tpl || !draft) return <main className="lc-page lc-stagger"><p className="lc-hint">Chargement…</p></main>

  const isEmail = tpl.channel === 'EMAIL'

  return (
    <main className="lc-page lc-stagger">
      <SettingsCrumb />
      <button className="lc-btn lc-btn--ghost" onClick={() => navigate('/settings/communications?tab=templates')}>
        <ChevronLeft size={14} aria-hidden="true" /> Modèles
      </button>
      <h1 className="lc-title">{tpl.name}</h1>
      <p className="lc-subtitle">
        {tpl.channel === 'EMAIL' ? 'Modèle e-mail' : tpl.channel === 'PUSH' ? 'Modèle Push (transport LOT 9.1)' : 'Modèle In-App'}
        {tpl.is_system ? ' · modèle système — personnalisable, ou dupliquez-le pour garder l’original' : ''}
      </p>

      <div className="cm-tpl-grid">
        <div className="cm-tpl-form">
          <section className="lc-panel gd-panel">
            <header className="lc-panel__head"><h2 className="lc-section-title">Contenu</h2></header>
            <div className="lc-panel__body ad-catalog__form">
              <label className="adm-field"><span>Nom du modèle</span>
                <input value={draft.name} disabled={!canManage} onChange={(e) => upd({ name: e.target.value })} /></label>
              {isEmail ? (
                <>
                  <label className="adm-field"><span>Sujet</span>
                    <input value={draft.subject ?? ''} disabled={!canManage} onChange={(e) => upd({ subject: e.target.value || null })} /></label>
                  <label className="adm-field"><span>Preheader (aperçu boîte de réception)</span>
                    <input value={draft.preheader ?? ''} disabled={!canManage} onChange={(e) => upd({ preheader: e.target.value || null })} /></label>
                </>
              ) : null}
              <label className="adm-field"><span>Titre</span>
                <input value={draft.title ?? ''} disabled={!canManage} onChange={(e) => upd({ title: e.target.value || null })} /></label>
              <label className="adm-field">
                <span style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  Corps du message
                  <button type="button" className="lc-btn lc-btn--ghost" onClick={() => setPicker((p) => !p)} aria-expanded={picker}>
                    <Plus size={13} aria-hidden="true" /> Insérer une variable
                  </button>
                </span>
                {picker ? (
                  <div className="cm-varpicker" role="menu">
                    {varGroups.map((g) => (
                      <div key={g.group} className="cm-varpicker__group">
                        <span className="lc-hint">{g.group}</span>
                        {g.items.map((v) => (
                          <button key={v.key} role="menuitem" className="cm-varpicker__item" onClick={() => insertVar(v.key)}>
                            {v.label} <span className="lc-mono lc-hint">{`{{${v.key}}}`}</span>
                          </button>
                        ))}
                      </div>
                    ))}
                  </div>
                ) : null}
                <textarea ref={bodyRef} rows={7} value={draft.body} disabled={!canManage}
                  onChange={(e) => upd({ body: e.target.value })} />
              </label>
              {isEmail ? (
                <>
                  <div className="rb-cond">
                    <label className="adm-field"><span>Texte du bouton</span>
                      <input value={draft.cta_label ?? ''} disabled={!canManage} onChange={(e) => upd({ cta_label: e.target.value || null })} /></label>
                    <label className="adm-field"><span>Lien du bouton</span>
                      <input value={draft.cta_link ?? ''} disabled={!canManage} placeholder={'{{alert.url}}'}
                        onChange={(e) => upd({ cta_link: e.target.value || null })} /></label>
                  </div>
                  <label className="adm-field"><span>Pied de page (vide = celui du thème)</span>
                    <input value={draft.footer ?? ''} disabled={!canManage} onChange={(e) => upd({ footer: e.target.value || null })} /></label>
                  <label className="adm-field"><span>Thème</span>
                    <select value={draft.theme_id ?? ''} disabled={!canManage} onChange={(e) => upd({ theme_id: e.target.value || null })}>
                      <option value="">Thème par défaut</option>
                      {themes.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select></label>
                </>
              ) : null}
            </div>
          </section>

          {isEmail ? (
            <section className="lc-panel gd-panel">
              <header className="lc-panel__head"><h2 className="lc-section-title">Blocs</h2></header>
              <div className="lc-panel__body lc-panel__body--flush">
                {blocks.map((b, i) => (
                  <div key={`${b.type}-${i}`} className="lc-action-row cm-block-row" style={{ cursor: 'default' }}>
                    <label className="ad-toggle" style={{ flex: 1, margin: 0 }}>
                      <input type="checkbox" checked={b.enabled} disabled={!canManage}
                        onChange={(e) => upd({ blocks: blocks.map((x, j) => j === i ? { ...x, enabled: e.target.checked } : x) })} />
                      <span>{BLOCK_LABELS[b.type] ?? b.type}</span>
                    </label>
                    <button className="lc-btn lc-btn--ghost" aria-label={`Monter ${BLOCK_LABELS[b.type]}`} disabled={i === 0 || !canManage}
                      onClick={() => moveBlock(i, -1)}><ArrowUp size={13} aria-hidden="true" /></button>
                    <button className="lc-btn lc-btn--ghost" aria-label={`Descendre ${BLOCK_LABELS[b.type]}`} disabled={i === blocks.length - 1 || !canManage}
                      onClick={() => moveBlock(i, 1)}><ArrowDown size={13} aria-hidden="true" /></button>
                  </div>
                ))}
              </div>
            </section>
          ) : null}
        </div>

        {isEmail ? (
          <aside className="cm-tpl-preview" aria-label="Aperçu">
            <div className="cm-tpl-preview__bar">
              <div className="lc-seg" role="tablist" aria-label="Mode d’aperçu">
                <button role="tab" aria-selected={mode === 'desktop'} className={`lc-seg__btn ${mode === 'desktop' ? 'lc-seg__btn--on' : ''}`}
                  onClick={() => setMode('desktop')}><Monitor size={13} aria-hidden="true" /> Desktop</button>
                <button role="tab" aria-selected={mode === 'mobile'} className={`lc-seg__btn ${mode === 'mobile' ? 'lc-seg__btn--on' : ''}`}
                  onClick={() => setMode('mobile')}><Smartphone size={13} aria-hidden="true" /> Mobile</button>
              </div>
              <button className="lc-btn lc-btn--ghost" onClick={() => setShowText((s) => !s)} aria-pressed={showText}>
                {showText ? 'Voir le HTML' : 'Voir le texte brut'}
              </button>
            </div>
            <p className="lc-hint">Aperçu avec des données d’exemple fictives. Sujet : <strong>{preview?.subject ?? '…'}</strong></p>
            {showText ? (
              <pre className="cm-tpl-preview__text">{preview?.text ?? '…'}</pre>
            ) : (
              <div className={`cm-tpl-preview__frame ${mode === 'mobile' ? 'cm-tpl-preview__frame--mobile' : ''}`}>
                {preview ? (
                  <iframe title="Aperçu de l’e-mail" sandbox="" srcDoc={preview.html} />
                ) : <p className="lc-hint" style={{ padding: 16 }}>Rendu en cours…</p>}
              </div>
            )}
          </aside>
        ) : (
          <aside className="cm-tpl-preview" aria-label="Aperçu">
            <p className="lc-hint">Aperçu (données fictives) :</p>
            <div className="lc-bell__pop" style={{ position: 'static', width: '100%' }}>
              <div className="lc-bell__item lc-bell__item--unread" style={{ cursor: 'default' }}>
                <span className="lc-dot lc-dot--critical" aria-hidden="true" />
                <span className="lc-stack lc-bell__body">
                  <span className="lc-bell__title">{(draft.title ?? '').replace(/\{\{service\.name\}\}/g, 'UniFi Protect').replace(/\{\{alert\.title\}\}/g, 'Exemple — UniFi Protect indisponible') || 'Titre'}</span>
                  <span className="lc-hint">{draft.body.replace(/\{\{[^}]+\}\}/g, (m) => ({ '{{service.name}}': 'UniFi Protect', '{{alert.duration}}': '8 min 21 s', '{{metric.label}}': 'Caméras en ligne', '{{metric.value}}': '15', '{{metric.unit}}': 'caméra' }[m] ?? '…'))}</span>
                </span>
              </div>
            </div>
          </aside>
        )}
      </div>
    </main>
  )
}

export function TemplateEditorPage({ templateId }: { templateId: string }) {
  const meta = useShellMeta()
  return (
    <ToastProvider>
      <Shell active="settings" meta={meta}>
        <Inner templateId={templateId} />
      </Shell>
    </ToastProvider>
  )
}
